/** @jsx jsx */

import { Fragment, memo, useEffect, useRef } from "react"
import { Box, Button, jsx, Spinner } from "theme-ui"
import Helmet from "react-helmet"
import { HeaderSpacing } from "./page"
import { Trans } from "react-i18next"
import { useTextDirection } from "../i18n"
import { colourMidpoint, getHexType } from "../data/methods"
import qs from "query-string"
import { Link as GatsbyLink } from "gatsby"
import { ActionBoxStyling, ActionSidebarStyling } from "../data/css"

export function isActionBox(anType: string) {
  const actionBoxTypes = ["fundraiser", "petition"]

  return actionBoxTypes.includes(anType)
}

export function isActionSidebar(anType: string) {
  const actionSidebarTypes = ["event"]

  return actionSidebarTypes.includes(anType)
}

function oneTimeReload() {
  if (window.localStorage) {
    if (!localStorage.getItem("firstRefresh")) {
      localStorage["firstRefresh"] = true
      window.location.reload()
    } else {
      localStorage.removeItem("firstRefresh")
    }
  }
}

export class ActionData {
  anLink: string
  anIdentifier: string
  anSize: string
  anType: string

  constructor(
    anLink: string,
    anIdentifier: string,
    anSize: string,
    anType: string
  ) {
    this.anLink = anLink
    this.anIdentifier = anIdentifier
    this.anSize = anSize
    this.anType = anType
  }

  isTruthy() {
    return this.anLink && this.anIdentifier && this.anSize && this.anType
  }
}

export const SupportAction: React.FC<{ language: string }> = memo(
  ({ language }) => {
    let attempts = 0

    const timerId = useRef(setTimeout(() => function() {}, 0))

    const showExtraText = () => {
      const divIframeHolder = document.getElementById("div-iframe-holder")
      const extraTextHolder = document.getElementById("extra-text")
      const formCover = document.getElementById("form-cover")

      if (
        divIframeHolder &&
        extraTextHolder &&
        formCover &&
        divIframeHolder.firstChild?.firstChild
      ) {
        if (extraTextHolder.classList.contains("hidden")) {
          extraTextHolder.classList.remove("hidden")
        }
        if (!formCover.classList.contains("hidden")) {
          formCover.classList.add("hidden")
        }
      } else if (formCover) {
        if (!formCover.classList.contains("loaded")) {
          formCover.classList.add("loaded")
        }
        if (formCover.classList.contains("hidden")) {
          formCover.classList.remove("hidden")
        }
      }
    }

    const resetExtraText = () => {
      const extraTextHolder = document.getElementById("extra-text")
      const formCover = document.getElementById("form-cover")

      if (extraTextHolder && formCover) {
        if (!extraTextHolder.classList.contains("hidden")) {
          extraTextHolder.classList.add("hidden")
        }

        if (formCover.classList.contains("loaded")) {
          formCover.classList.remove("loaded")
        }

        if (formCover.classList.contains("hidden")) {
          formCover.classList.remove("hidden")
        }
      }
    }

    const handleSupportAction = () => {
      const scriptHolder = document.getElementById("script-holder")
      const divIframeHolder = document.getElementById("div-iframe-holder")

      if (scriptHolder && divIframeHolder) {
        let scriptSrc =
          "https://actionnetwork.org/widgets/v4/fundraising/support-the-progressive-international?format=js&source=widget&css=false"
        let divId = "can-fundraising-area-support-the-progressive-international"

        if (language == "es") {
          scriptSrc =
            "https://actionnetwork.org/widgets/v4/fundraising/me-gustaria-contribuir-2?format=js&source=widget&css=false"
          divId = "can-fundraising-area-me-gustaria-contribuir-2"
        } else if (language == "de") {
          scriptSrc =
            "https://actionnetwork.org/widgets/v4/fundraising/ich-wurde-gerne-folgenden-beitrag-leisten?format=js&source=widget&css=false"
          divId =
            "can-fundraising-area-ich-wurde-gerne-folgenden-beitrag-leisten"
        } else if (language == "pt-BR") {
          scriptSrc =
            "https://actionnetwork.org/widgets/v4/fundraising/eu-gostaria-de-contribuir?format=js&source=widget&css=false"
          divId = "can-fundraising-area-eu-gostaria-de-contribuir"
        } else if (language == "fr") {
          scriptSrc =
            "https://actionnetwork.org/widgets/v4/fundraising/je-souhaite-contribuer?format=js&source=widget&css=false"
          divId = "can-fundraising-area-je-souhaite-contribuer"
        }

        scriptHolder.innerHTML = ""
        divIframeHolder.innerHTML = ""

        const divTag = document.createElement("div")
        divTag.style.width = "100%"
        divTag.id = divId

        divIframeHolder.appendChild(divTag)

        const scriptTag = document.createElement("script")
        scriptTag.src = scriptSrc

        scriptHolder.appendChild(scriptTag)

        //console.log("Cleared in handleSupportAction.");
        clearTimeout(timerId.current)

        timerId.current = setTimeout(function() {
          if (attempts < 5 && divTag && divTag.innerHTML == "") {
            attempts = attempts + 1
            handleSupportAction()
          } else {
            showExtraText()
          }
        }, 2500)
        //console.log("Set in handleSupportAction.");
      }
    }

    const cleanActions = () => {
      const scriptHolder = document.getElementById("script-holder")
      const divIframeHolder = document.getElementById("div-iframe-holder")

      if (scriptHolder && divIframeHolder) {
        scriptHolder.innerHTML = ""
        divIframeHolder.innerHTML = ""
      }
    }

    useEffect(() => {
      try {
        oneTimeReload()
      } catch (err) {
        //console.log("Reload issue.");
      }
    }, [language])

    useEffect(() => {
      resetExtraText()

      //console.log("Cleared in useEffect.");
      clearTimeout(timerId.current)

      timerId.current = setTimeout(function() {
        handleSupportAction()
      }, 2000)
      //console.log("Set in useEffect.");

      return function cleanup() {
        //console.log("Cleared and cleaned in useEffect cleanup.");
        clearTimeout(timerId.current)
        cleanActions()
      }
    })

    return (
      <Fragment>
        <Helmet
          style={[
            {
              cssText: `
              .can_embed #can_embed_form {
                background-color: #F7F4F1 !important;
              }
              
              .can_embed .line,
              .can_embed #action_info,
              .can_embed #logo_wrap,
              .can_embed #form_col1 #donation_recipient_wrap,
              .can_embed #form_col1 #donate_login,
              .can_embed div.affirmative_note {
                display: none;
              }
              
              .can_embed #form_col1 #donation_recipient_wrap::after {
                content: "Your details";
                display: unset;
              }

              .can_embed #form_col1 #donation_welcome {
                margin-top: 10px;
                background-color: rgb(252 22 75 / 20%);
                padding: 1.2em;
                border-radius: 15px;
              }
              
              .can_embed #form_col1 .donate_amount_wrap {
                display: grid;
                grid-template-columns: 1fr 1fr 1fr 1fr;
                gap: 0.2em;
                padding-right: 1px;
                position: relative;
              }
              
              .can_embed #form_col1 .donate_amount_wrap label {
                padding: 15px 5px 15px 5px;
                background-color: white;
                border: 2px solid #FC164B;
                border-radius: 8px;
                text-align: center;
                font-size: 18px;
                align-self: center;
                height: 55px;
                font-weight: 500;
                cursor: pointer;
                white-space: nowrap;
                line-height: 24px;
              }
              
              .can_embed #form_col1 .donate_amount_wrap label:hover,
              .can_embed #form_col1 .donate_amount_wrap .donate_amount-selected {
                background-color: #FC164B;
                color: white;
              }
              
              .can_embed #form_col1 .donate_amount_wrap label:active {
                background-color: darkred;
                border-color: darkred;
              }
              
              .can_embed #form_col1 .donate_amount_wrap input[type=radio] {
                visibility: hidden;
                position: absolute;
                top: 2px;
                left: 2px;
              }
              
              .can_embed #form_col1 .donate_amount_wrap input[type=text] {
                max-width: 50px;
                line-height: 18px;
                font-size: 18px;
                height: 20px;
                border-width: 0px;
                font-weight: 500;
                background-color: inherit;
                color: inherit;
                text-align: center;
              }
              
              .can_embed #form_col1 .donate_recurring {
                line-height: 58px;
                color: #280000;
                margin-bottom: 30px;
                margin-top: 2.5px;
                height: 60px;
                width: 100%;
                -webkit-user-select: none;    
                -moz-user-select: none;
                -ms-user-select: none;
                user-select: none;
                cursor: pointer;
                position: relative;
              }

              .can_embed #form_col1 .donate_recurring .donate_recurring_inner {
                position: absolute;
                z-index: 1;
                width: 100%;
              }

              .can_embed #form_col1 .donate_recurring .donate_recurring_inner label {
                position: absolute;
                width: 100%;
                text-align: center;
                cursor: pointer !important;
                font-size: 18px
              }

              .can_embed #form_col1 .donate_recurring .donate_recurring_inner label[autofocus=false] {
                background-color: red;
              }
              
              .can_embed #form_col1 .donate_recurring input[type=checkbox] {
                vertical-align: middle;
                appearance: none;
                font-size: 60px;
                background: white;
                border: 2px solid #FC164B;
                width: 100%;
                height: 55px;
                border-radius: 8px;
                -webkit-box-pack: center;
                justify-content: center;
                -webkit-box-align: center;
                margin: 0px;
                position: absolute;
                z-index: -1;
                left: 0px;
              }
              
              .can_embed #form_col1 .donate_recurring input[type=checkbox]:checked {
                border: 6px solid #FC164B;
              }
              
              .can_embed #form_col1 .country_wrap {
                margin-top: 16px;
                display: inline-block;
                width: 100%;
                margin-bottom: 30px;
              }

              .can_embed #form_col1 .country_wrap > span > a.international_link {
                font-family: "IBM Plex Mono", Menlo, monospace;
                color: #FC164B;
                text-decoration: underline;
              }

              .can_embed #form_col1 .country_wrap > span > a.international_link:hover {
                font-weight: 600;
                cursor: pointer;
              }
              
              .can_embed #form_col1 .country_wrap .country_drop_wrap {
                border: 1px solid black;
                margin: 0px;
                width: 100%;
                display: block;
                padding: 7.5px;
                border-radius: 4px;
              }

              .can_embed #form_col1 .country_wrap .country_drop_wrap #form-country {
                width: 100% !important;
                height: 20px !important;
              }

              .can_embed #form_col1 .country_wrap .country_drop_wrap span.can_selectInner {
                white-space: nowrap;
              }
              
              .can_embed #form_col1 .country_wrap .country_drop_wrap select {
                width: 100%;
              }
              
              .can_embed #form_col1 .floatlabel-wrapper {
                margin-top: 15px;
                display: inline-block;
                margin-bottom: 5px;
                width: 100%;
              }

              .can_embed #form_col1 .state_wrap {
                margin-top: 15px;
                white-space: nowrap;
                width: 100%;
                border: 1px solid black;
                display: block;
                padding: 7.5px;
                border-radius: 4px;
              }

              .can_embed #form_col1 .state_wrap select#form-state {
                height: 20px !important;
                width: 100% !important;
              }
              
              .can_embed #form_col1 .floatlabel-wrapper label {
                width: 100%;
                color: #280000;
                font-size: 18px;
                font-weight: 600;
                letter-spacing: -0.3px;
              }
              
              .can_embed #form_col1 .floatlabel-wrapper input {
                width: 100%;
                appearance: none;
                border-bottom-width: 2px;
                border-bottom-style: solid;
                border-bottom-color: #280000;
                border-top: none;
                border-left: none;
                border-right: none;
                font-size: 16px;
                background-color: rgba(0, 0, 0, 0);
                line-height: 22px;
                font-family: neue-haas-grotesk-display, "Neue Haas", "Helvetica Neue", Helvetica, system-ui, sans-serif;
                margin-top: 8px;
              }
              
              .can_embed #form_col1 .floatlabel-wrapper input:focus {
                outline: none;
              }
              
              .can_embed #form_col1 .floatlabel-wrapper input::placeholder,
              .can_embed #form_col1 .floatlabel-wrapper input::-webkit-input-placeholder,
              .can_embed #form_col1 .floatlabel-wrapper input:-moz-placeholder,
              .can_embed #form_col1 .floatlabel-wrapper input::-moz-placeholder,
              .can_embed #form_col1 .floatlabel-wrapper input:-ms-input-placeholder,
              .can_embed #form_col2 .floatlabel-wrapper input::placeholder,
              .can_embed #form_col2 .floatlabel-wrapper input::-webkit-input-placeholder,
              .can_embed #form_col2 .floatlabel-wrapper input:-moz-placeholder,
              .can_embed #form_col2 .floatlabel-wrapper input::-moz-placeholder,
              .can_embed #form_col2 .floatlabel-wrapper input:-ms-input-placeholder {
                opacity: 0;
              }
              
              .can_embed #form_col2 {
                margin-top: 45px;
              }
              
              .can_embed #form_col2>div.clearfix {
                padding: 10px;
                padding-left: 0px;
                height: 54.7px;
                width: 55%;
                margin-top: 38px;
                font-size: 17px;
                display: inline-block;
              }
              
              .can_embed #form_col2>div.clearfix #form-month,
              .can_embed #form_col2>div.clearfix #form-year {
                width: 70px;
                height: 18px !important;
              }
              
              .can_embed #form_col2>div.clearfix span.donate_cc_month {
                border-bottom: 2px solid black;
                margin-right: 10px;
              }
              
              .can_embed #form_col2>div.clearfix span.donate_cc_month span.can_select {
                width: 55px;
              }
              
              .can_embed #form_col2>div.clearfix span.donate_cc_month span.can_select span.can_selectInner,
              .can_embed #form_col2>div.clearfix span.donate_cc_year span.can_select span.can_selectInner {
                width: 70px;
              }
              
              .can_embed #form_col2>div.clearfix span.donate_cc_year {
                border-bottom: 2px solid black;
              }
              
              .can_embed #form_col2>div.clearfix span.donate_cc_year span.can_select {
                width: 60px;
              }
              
              .can_embed #form_col2 .floatlabel-wrapper {
                margin-top: 15px;
                margin-bottom: 5px;
                width: 45%;
                float: right;
              }
              
              .can_embed #form_col2 .floatlabel-wrapper:first-child {
                width: 100%;
                display: block;
                float: none;
              }
              
              .can_embed #form_col2 .floatlabel-wrapper label {
                width: 100%;
                color: #280000;
                font-size: 18px;
                font-weight: 600;
                letter-spacing: -0.4px;
              }
              
              .can_embed #form_col2 .floatlabel-wrapper input#form-card_sec {
                width: 94%;
              }
              
              .can_embed #form_col2 .floatlabel-wrapper input {
                width: 100%;
                appearance: none;
                border-bottom-width: 2px;
                border-bottom-style: solid;
                border-bottom-color: #280000;
                border-top: none;
                border-left: none;
                border-right: none;
                font-size: 16px;
                background-color: rgba(0, 0, 0, 0);
                line-height: 22px;
                font-family: neue-haas-grotesk-display, "Neue Haas", "Helvetica Neue", Helvetica, system-ui, sans-serif;
                margin-top: 8px;
              }
              
              .can_embed #form_col2 li.form_builder_output {
                display: none;
              }
              
              .can_embed #form_col2 #donate_form_button {
                margin-top: 15px;
                appearance: none;
                font-size: 18px;
                text-align: center;
                line-height: 20px;
                padding: 8px 16px;
                background-color: #ffffff;
                border: 2px solid #FC164B;
                color: #FC164B;
                border-radius: 100px;
                text-decoration: none;
                font-weight: 500;
                user-select: none;
                outline: none;
                cursor: pointer;
                letter-spacing: -0.3px;
                white-space: break-spaces;
              }

              .can_embed #form_col2 #d_sharing {
                color: #280000;
              }
              
              .can_embed #form_col2 #d_sharing::marker {
                font-size: 0;
              }

              .can_embed #form_col2 #d_sharing ul,
              .can_embed #form_col2 #d_sharing li {
                padding-left: 0px;
              }

              .can_embed #form_col2 #d_sharing .control-group::marker {
                font-size: 0;
              }

              .can_embed #form_col2 #d_sharing .control-group .control-label{
                margin-bottom: 10px;
              }

              .can_embed #form_col2 #d_sharing .control-group span.controls.check_radio_field {
                display: block;
                margin-bottom: 6px;
              }

              .can_embed #form_col2 #d_sharing .control-group span.controls.check_radio_field > :first-child {
                margin-right: 8px;
                margin-bottom: 4px;
              }

              .can_embed #form_col2 #d_sharing .control-group span.controls.check_radio_field label.radio {
                white-space: nowrap;
                line-height: 35px;
              }

              .can_embed #form_col2 #d_sharing .control-group span.controls.check_radio_field label.radio input[type=radio],
              .can_embed #form_col2 > li.control-group#mobile_opt_in input[type=checkbox] {
                vertical-align: middle;
                cursor: pointer;
                appearance: none;
                font-size: 60px;
                background: white;
                border: 2px solid white;
                outline: 2px solid #FC164B;
                width: 20px;
                height: 20px;
                border-radius: 20%;
                -webkit-box-pack: center;
                justify-content: center;
                -webkit-box-align: center;
                margin: 2px;
                margin-left: 1.5px;
                margin-right: 5px;
              }

              .can_embed #form_col2 #d_sharing .control-group span.controls.check_radio_field label.radio input[type=radio]:checked,
              .can_embed #form_col2 > li.control-group#mobile_opt_in input[type=checkbox]:checked {
                background: #FC164B;
                border: 2px solid #FED0DB;
                outline: 2px solid #FC164B;
              }
              
              .can_embed #form_col2 img.ajax-loading {
                display: none;
              }

              .can_embed #form_col2 > li.core_field::marker,
              .can_embed #form_col2 > li#mobile_opt_in::marker {
                color: transparent;
                font-size: 0.3px;
              }

              .can_embed div#donation_welcome > img {
                display: none;
              }

              .can_embed div#donation_welcome div.left {
                font-size: 1.1em;
              }

              .can_embed div#donation_welcome div.left div {
                margin-bottom: 15px;
              }

              .can_embed div#donation_welcome div.left div.wrong_card {
                margin-bottom: 0px;
              }

              .can_embed div#donation_welcome #donation_welcome-login {
                margin-top: 15px;
              }

              .can_embed div#donation_welcome a {
                color: #280000;
                text-decoration-color: #FC164B;
              }

              .can_embed div#donation_welcome a:hover {
                color: #FC164B;
              }
              
              #form-styling-container {
                position: relative;
                height: 100%;
                width: 100%;
                min-height: 200px;
              }
              
              #form-styling-container #form-cover.hidden,
              #form-styling-container #form-cover.loaded #loading-text,
              #form-styling-container #form-cover #error-message {
                display: none;
              }
              
              #form-styling-container #form-cover.loaded #error-message {
                display: block;
              }
              
              #form-styling-container #form-cover {
                position: absolute;
                height: 100%;
                width: 100%;
                background-color: #F7F4F1;
              }
              
              #form-styling-container #form-cover #loading-text {
                width: 100%;
                text-align: center;
                font-size: 30px;
                line-height: 50px;
                color: #280000;
              }
              
              #form-styling-container #form-cover #error-message {
                width: 100%;
                text-align: center;
                font-size: 30px;
                line-height: 50px;
              }
              
              #extra-text {
                position: relative;
                width: 100%;
                margin-top: 10px;
              }
              
              #extra-text.hidden .main-heading,
              #extra-text.hidden .details-heading {
                display: none;
              }
              
              #extra-text .main-heading {
                font-size: 24px;
                font-weight: 600;
                line-height: 110%;
                letter-spacing: -0.3px;
                margin-bottom: 0.5em;
              }
              
              #extra-text .details-heading {
                font-size: 24px;
                font-weight: 600;
                line-height: 110%;
                letter-spacing: -0.3px;
                margin-bottom: 0.5em;
                position: absolute;
                left: 0px;
                top: 164px;
              }
              
              /*iframe#can_embed_iframe {
                height: 1020px !important;
              }*/
              
              @media screen and (max-width: 315px) {
                .can_embed #form_col1 .donate_amount_wrap label {
                  white-space: normal;
                  padding-top: 3px;
                }
              
                .can_embed #form_col1 .donate_amount_wrap label.other_label {
                  white-space: nowrap;
                  padding-top: 15px;
                }
              }
              `,
            },
          ]}
        />
        <HeaderSpacing sx={{ display: ["none", null, "block"] }} />
        <div id="form-styling-container">
          <div id="script-holder"></div>
          <div id="extra-text" className="hidden">
            <div className="main-heading">
              <Trans>I would like to contribute</Trans>
            </div>
            <div className="details-heading"></div>
          </div>
          <div id="form-cover">
            <div id="loading-text">
              <Spinner />
            </div>
            <div id="error-message" className="hidden">
              There was a problem loading the donation form. Please try
              refreshing the page.
            </div>
          </div>
          <div id="div-iframe-holder"></div>
        </div>
      </Fragment>
    )
  }
)

export const ContributeAction: React.FC<{ language: string }> = memo(
  ({ language }) => {
    let attempts = 0

    const timerId = useRef(setTimeout(() => function() {}, 0))

    const showExtraText = () => {
      const divIframeHolder = document.getElementById("div-iframe-holder")
      const extraTextHolder = document.getElementById("extra-text")
      const formCover = document.getElementById("form-cover")

      if (
        divIframeHolder &&
        extraTextHolder &&
        formCover &&
        divIframeHolder.firstChild?.firstChild
      ) {
        if (extraTextHolder.classList.contains("hidden")) {
          extraTextHolder.classList.remove("hidden")
        }
        if (!formCover.classList.contains("hidden")) {
          formCover.classList.add("hidden")
        }
      } else if (formCover) {
        if (!formCover.classList.contains("loaded")) {
          formCover.classList.add("loaded")
        }
        if (formCover.classList.contains("hidden")) {
          formCover.classList.remove("hidden")
        }
      }
    }

    const resetExtraText = () => {
      const extraTextHolder = document.getElementById("extra-text")
      const formCover = document.getElementById("form-cover")

      if (extraTextHolder && formCover) {
        if (!extraTextHolder.classList.contains("hidden")) {
          extraTextHolder.classList.add("hidden")
        }

        if (formCover.classList.contains("loaded")) {
          formCover.classList.remove("loaded")
        }

        if (formCover.classList.contains("hidden")) {
          formCover.classList.remove("hidden")
        }
      }
    }

    const handleSupportAction = () => {
      const scriptHolder = document.getElementById("script-holder")
      const divIframeHolder = document.getElementById("div-iframe-holder")

      if (scriptHolder && divIframeHolder) {
        let scriptSrc =
          "https://actionnetwork.org/widgets/v4/fundraising/i-would-like-to-contribute?format=js&source=widget&css=false"
        let divId = "can-fundraising-area-i-would-like-to-contribute"

        if (language == "es") {
          scriptSrc =
            "https://actionnetwork.org/widgets/v4/fundraising/me-gustaria-contribuir-3?format=js&source=widget&css=false"
          divId = "can-fundraising-area-me-gustaria-contribuir-3"
        } else if (language == "de") {
          scriptSrc =
            "https://actionnetwork.org/widgets/v4/fundraising/ich-wurde-gerne-folgenden-beitrag-leisten-2?format=js&source=widget&css=false"
          divId =
            "can-fundraising-area-ich-wurde-gerne-folgenden-beitrag-leisten-2"
        } else if (language == "pt-BR") {
          scriptSrc =
            "https://actionnetwork.org/widgets/v4/fundraising/eu-gostaria-de-contribuir-2?format=js&source=widget&css=false"
          divId = "can-fundraising-area-eu-gostaria-de-contribuir-2"
        } else if (language == "fr") {
          scriptSrc =
            "https://actionnetwork.org/widgets/v4/fundraising/je-souhaite-contribuer-2?format=js&source=widget&css=false"
          divId = "can-fundraising-area-je-souhaite-contribuer-2"
        }

        scriptHolder.innerHTML = ""
        divIframeHolder.innerHTML = ""

        const divTag = document.createElement("div")
        divTag.style.width = "100%"
        divTag.id = divId

        divIframeHolder.appendChild(divTag)

        const scriptTag = document.createElement("script")
        scriptTag.src = scriptSrc

        scriptHolder.appendChild(scriptTag)

        //console.log("Cleared in handleSupportAction.");
        clearTimeout(timerId.current)

        timerId.current = setTimeout(function() {
          if (attempts < 5 && divTag && divTag.innerHTML == "") {
            attempts = attempts + 1
            handleSupportAction()
          } else {
            showExtraText()
          }
        }, 2500)
        //console.log("Set in handleSupportAction.");
      }
    }

    const cleanActions = () => {
      const scriptHolder = document.getElementById("script-holder")
      const divIframeHolder = document.getElementById("div-iframe-holder")

      if (scriptHolder && divIframeHolder) {
        scriptHolder.innerHTML = ""
        divIframeHolder.innerHTML = ""
      }
    }

    useEffect(() => {
      try {
        oneTimeReload()
      } catch (err) {
        //console.log("Reload issue.");
      }
    }, [language])

    useEffect(() => {
      resetExtraText()

      //console.log("Cleared in useEffect.");
      clearTimeout(timerId.current)

      timerId.current = setTimeout(function() {
        handleSupportAction()
      }, 2000)
      //console.log("Set in useEffect.");

      return function cleanup() {
        //console.log("cleared and cleaned in useEffect cleanup.");
        clearTimeout(timerId.current)
        cleanActions()
      }
    })

    return (
      <Fragment>
        <Helmet
          style={[
            {
              cssText: `
              .can_embed #can_embed_form {
                background-color: #F7F4F1 !important;
              }
              
              .can_embed .line,
              .can_embed #action_info,
              .can_embed #logo_wrap,
              .can_embed #form_col1 #donation_recipient_wrap,
              .can_embed #form_col1 #donate_login,
              .can_embed div.affirmative_note {
                display: none;
              }
              
              .can_embed #form_col1 #donation_recipient_wrap::after {
                content: "Your details";
                display: unset;
              }

              .can_embed #form_col1 #donation_welcome {
                margin-top: 10px;
                background-color: rgb(252 22 75 / 20%);
                padding: 1.2em;
                border-radius: 15px;
              }
              
              .can_embed #form_col1 .donate_amount_wrap {
                display: grid;
                grid-template-columns: 1fr 1fr 1fr 1fr;
                gap: 0.2em;
                padding-right: 1px;
                position: relative;
              }
              
              .can_embed #form_col1 .donate_amount_wrap label {
                padding: 15px 5px 15px 5px;
                background-color: white;
                border: 2px solid #FC164B;
                border-radius: 8px;
                text-align: center;
                font-size: 18px;
                align-self: center;
                height: 55px;
                font-weight: 500;
                cursor: pointer;
                white-space: nowrap;
                line-height: 24px;
              }
              
              .can_embed #form_col1 .donate_amount_wrap label:hover,
              .can_embed #form_col1 .donate_amount_wrap .donate_amount-selected {
                background-color: #FC164B;
                color: white;
              }
              
              .can_embed #form_col1 .donate_amount_wrap label:active {
                background-color: darkred;
                border-color: darkred;
              }
              
              .can_embed #form_col1 .donate_amount_wrap input[type=radio] {
                visibility: hidden;
                position: absolute;
                top: 2px;
                left: 2px;
              }
              
              .can_embed #form_col1 .donate_amount_wrap input[type=text] {
                max-width: 50px;
                line-height: 18px;
                font-size: 18px;
                height: 20px;
                border-width: 0px;
                font-weight: 500;
                background-color: inherit;
                color: inherit;
                text-align: center;
              }
              
              .can_embed #form_col1 .donate_recurring {
                line-height: 58px;
                color: #280000;
                margin-bottom: 30px;
                margin-top: 2.5px;
                height: 60px;
                width: 100%;
                -webkit-user-select: none;    
                -moz-user-select: none;
                -ms-user-select: none;
                user-select: none;
                cursor: pointer;
                position: relative;
              }

              .can_embed #form_col1 .donate_recurring .donate_recurring_inner {
                position: absolute;
                z-index: 1;
                width: 100%;
              }

              .can_embed #form_col1 .donate_recurring .donate_recurring_inner label {
                position: absolute;
                width: 100%;
                text-align: center;
                cursor: pointer !important;
                font-size: 18px
              }

              .can_embed #form_col1 .donate_recurring .donate_recurring_inner label[autofocus=false] {
                background-color: red;
              }
              
              .can_embed #form_col1 .donate_recurring input[type=checkbox] {
                vertical-align: middle;
                appearance: none;
                font-size: 60px;
                background: white;
                border: 2px solid #FC164B;
                width: 100%;
                height: 55px;
                border-radius: 8px;
                -webkit-box-pack: center;
                justify-content: center;
                -webkit-box-align: center;
                margin: 0px;
                position: absolute;
                z-index: -1;
                left: 0px;
              }
              
              .can_embed #form_col1 .donate_recurring input[type=checkbox]:checked {
                border: 6px solid #FC164B;
              }
              
              .can_embed #form_col1 .country_wrap {
                margin-top: 16px;
                display: inline-block;
                width: 100%;
                margin-bottom: 30px;
              }

              .can_embed #form_col1 .country_wrap > span > a.international_link {
                font-family: "IBM Plex Mono", Menlo, monospace;
                color: #FC164B;
                text-decoration: underline;
              }

              .can_embed #form_col1 .country_wrap > span > a.international_link:hover {
                font-weight: 600;
                cursor: pointer;
              }
              
              .can_embed #form_col1 .country_wrap .country_drop_wrap {
                border: 1px solid black;
                margin: 0px;
                width: 100%;
                display: block;
                padding: 7.5px;
                border-radius: 4px;
              }

              .can_embed #form_col1 .country_wrap .country_drop_wrap #form-country {
                width: 100% !important;
                height: 20px !important;
              }

              .can_embed #form_col1 .country_wrap .country_drop_wrap span.can_selectInner {
                white-space: nowrap;
              }
              
              .can_embed #form_col1 .country_wrap .country_drop_wrap select {
                width: 100%;
              }
              
              .can_embed #form_col1 .floatlabel-wrapper {
                margin-top: 15px;
                display: inline-block;
                margin-bottom: 5px;
                width: 100%;
              }

              .can_embed #form_col1 .state_wrap {
                margin-top: 15px;
                white-space: nowrap;
                width: 100%;
                border: 1px solid black;
                display: block;
                padding: 7.5px;
                border-radius: 4px;
              }

              .can_embed #form_col1 .state_wrap select#form-state {
                height: 20px !important;
                width: 100% !important;
              }
              
              .can_embed #form_col1 .floatlabel-wrapper label {
                width: 100%;
                color: #280000;
                font-size: 18px;
                font-weight: 600;
                letter-spacing: -0.3px;
              }
              
              .can_embed #form_col1 .floatlabel-wrapper input {
                width: 100%;
                appearance: none;
                border-bottom-width: 2px;
                border-bottom-style: solid;
                border-bottom-color: #280000;
                border-top: none;
                border-left: none;
                border-right: none;
                font-size: 16px;
                background-color: rgba(0, 0, 0, 0);
                line-height: 22px;
                font-family: neue-haas-grotesk-display, "Neue Haas", "Helvetica Neue", Helvetica, system-ui, sans-serif;
                margin-top: 8px;
              }
              
              .can_embed #form_col1 .floatlabel-wrapper input:focus {
                outline: none;
              }
              
              .can_embed #form_col1 .floatlabel-wrapper input::placeholder,
              .can_embed #form_col1 .floatlabel-wrapper input::-webkit-input-placeholder,
              .can_embed #form_col1 .floatlabel-wrapper input:-moz-placeholder,
              .can_embed #form_col1 .floatlabel-wrapper input::-moz-placeholder,
              .can_embed #form_col1 .floatlabel-wrapper input:-ms-input-placeholder,
              .can_embed #form_col2 .floatlabel-wrapper input::placeholder,
              .can_embed #form_col2 .floatlabel-wrapper input::-webkit-input-placeholder,
              .can_embed #form_col2 .floatlabel-wrapper input:-moz-placeholder,
              .can_embed #form_col2 .floatlabel-wrapper input::-moz-placeholder,
              .can_embed #form_col2 .floatlabel-wrapper input:-ms-input-placeholder {
                opacity: 0;
              }
              
              .can_embed #form_col2 {
                margin-top: 45px;
              }
              
              .can_embed #form_col2>div.clearfix {
                padding: 10px;
                padding-left: 0px;
                height: 54.7px;
                width: 55%;
                margin-top: 38px;
                font-size: 17px;
                display: inline-block;
              }
              
              .can_embed #form_col2>div.clearfix #form-month,
              .can_embed #form_col2>div.clearfix #form-year {
                width: 70px;
                height: 18px !important;
              }
              
              .can_embed #form_col2>div.clearfix span.donate_cc_month {
                border-bottom: 2px solid black;
                margin-right: 10px;
              }
              
              .can_embed #form_col2>div.clearfix span.donate_cc_month span.can_select {
                width: 55px;
              }
              
              .can_embed #form_col2>div.clearfix span.donate_cc_month span.can_select span.can_selectInner,
              .can_embed #form_col2>div.clearfix span.donate_cc_year span.can_select span.can_selectInner {
                width: 70px;
              }
              
              .can_embed #form_col2>div.clearfix span.donate_cc_year {
                border-bottom: 2px solid black;
              }
              
              .can_embed #form_col2>div.clearfix span.donate_cc_year span.can_select {
                width: 60px;
              }
              
              .can_embed #form_col2 .floatlabel-wrapper {
                margin-top: 15px;
                margin-bottom: 5px;
                width: 45%;
                float: right;
              }
              
              .can_embed #form_col2 .floatlabel-wrapper:first-child {
                width: 100%;
                display: block;
                float: none;
              }
              
              .can_embed #form_col2 .floatlabel-wrapper label {
                width: 100%;
                color: #280000;
                font-size: 18px;
                font-weight: 600;
                letter-spacing: -0.4px;
              }
              
              .can_embed #form_col2 .floatlabel-wrapper input#form-card_sec {
                width: 94%;
              }
              
              .can_embed #form_col2 .floatlabel-wrapper input {
                width: 100%;
                appearance: none;
                border-bottom-width: 2px;
                border-bottom-style: solid;
                border-bottom-color: #280000;
                border-top: none;
                border-left: none;
                border-right: none;
                font-size: 16px;
                background-color: rgba(0, 0, 0, 0);
                line-height: 22px;
                font-family: neue-haas-grotesk-display, "Neue Haas", "Helvetica Neue", Helvetica, system-ui, sans-serif;
                margin-top: 8px;
              }
              
              .can_embed #form_col2 li.form_builder_output {
                display: none;
              }
              
              .can_embed #form_col2 #donate_form_button {
                margin-top: 15px;
                appearance: none;
                font-size: 18px;
                text-align: center;
                line-height: 20px;
                padding: 8px 16px;
                background-color: #ffffff;
                border: 2px solid #FC164B;
                color: #FC164B;
                border-radius: 100px;
                text-decoration: none;
                font-weight: 500;
                user-select: none;
                outline: none;
                cursor: pointer;
                letter-spacing: -0.3px;
                white-space: break-spaces;
              }

              .can_embed #form_col2 #d_sharing {
                color: #280000;
              }
              
              .can_embed #form_col2 #d_sharing::marker {
                font-size: 0;
              }

              .can_embed #form_col2 #d_sharing ul,
              .can_embed #form_col2 #d_sharing li {
                padding-left: 0px;
              }

              .can_embed #form_col2 #d_sharing .control-group::marker {
                font-size: 0;
              }

              .can_embed #form_col2 #d_sharing .control-group .control-label{
                margin-bottom: 10px;
              }

              .can_embed #form_col2 #d_sharing .control-group span.controls.check_radio_field {
                display: block;
                margin-bottom: 6px;
              }

              .can_embed #form_col2 #d_sharing .control-group span.controls.check_radio_field > :first-child {
                margin-right: 8px;
                margin-bottom: 4px;
              }

              .can_embed #form_col2 #d_sharing .control-group span.controls.check_radio_field label.radio {
                white-space: nowrap;
                line-height: 35px;
              }

              .can_embed #form_col2 #d_sharing .control-group span.controls.check_radio_field label.radio input[type=radio] {
                vertical-align: middle;
                cursor: pointer;
                appearance: none;
                font-size: 60px;
                background: white;
                border: 2px solid white;
                outline: 2px solid #FC164B;
                width: 20px;
                height: 20px;
                border-radius: 20%;
                -webkit-box-pack: center;
                justify-content: center;
                -webkit-box-align: center;
                margin: 2px;
                margin-left: 1.5px;
                margin-right: 5px;
              }

              .can_embed #form_col2 #d_sharing .control-group span.controls.check_radio_field label.radio input[type=radio]:checked {
                background: #FC164B;
                border: 2px solid #FED0DB;
                outline: 2px solid #FC164B;
              }
              
              .can_embed #form_col2 img.ajax-loading {
                display: none;
              }

              .can_embed div#donation_welcome > img {
                display: none;
              }
  
              .can_embed div#donation_welcome div.left {
                font-size: 1.1em;
              }
  
              .can_embed div#donation_welcome div.left div {
                margin-bottom: 15px;
              }
  
              .can_embed div#donation_welcome a {
                color: #280000;
                text-decoration-color: #FC164B;
              }
  
              .can_embed div#donation_welcome a:hover {
                color: #FC164B;
              }
              
              #form-styling-container {
                position: relative;
                height: 100%;
                width: 100%;
                min-height: 200px;
              }
              
              #form-styling-container #form-cover.hidden,
              #form-styling-container #form-cover.loaded #loading-text,
              #form-styling-container #form-cover #error-message {
                display: none;
              }
              
              #form-styling-container #form-cover.loaded #error-message {
                display: block;
              }
              
              #form-styling-container #form-cover {
                position: absolute;
                height: 100%;
                width: 100%;
                background-color: #F7F4F1;
              }
              
              #form-styling-container #form-cover #loading-text {
                width: 100%;
                text-align: center;
                font-size: 30px;
                line-height: 50px;
                color: #280000;
              }
              
              #form-styling-container #form-cover #error-message {
                width: 100%;
                text-align: center;
                font-size: 30px;
                line-height: 50px;
              }
              
              #extra-text {
                position: relative;
                width: 100%;
                margin-top: 10px;
              }
              
              #extra-text.hidden .main-heading,
              #extra-text.hidden .details-heading {
                display: none;
              }
              
              #extra-text .main-heading {
                font-size: 24px;
                font-weight: 600;
                line-height: 110%;
                letter-spacing: -0.3px;
                margin-bottom: 0.5em;
              }
              
              #extra-text .details-heading {
                font-size: 24px;
                font-weight: 600;
                line-height: 110%;
                letter-spacing: -0.3px;
                margin-bottom: 0.5em;
                position: absolute;
                left: 0px;
                top: 164px;
              }
              
              /*iframe#can_embed_iframe {
                height: 1020px !important;
              }*/
              
              @media screen and (max-width: 315px) {
                .can_embed #form_col1 .donate_amount_wrap label {
                  white-space: normal;
                  padding-top: 3px;
                }
              
                .can_embed #form_col1 .donate_amount_wrap label.other_label {
                  white-space: nowrap;
                  padding-top: 15px;
                }
              }
            `,
            },
          ]}
        />
        <div id="script-holder"></div>
        <div id="extra-text" className="hidden">
          <div className="main-heading">
            <Trans>I would like to contribute</Trans>
          </div>
          <div className="details-heading"></div>
        </div>
        <div id="form-cover">
          <div id="loading-text">
            <Spinner />
          </div>
          <div id="error-message" className="hidden">
            <Trans>
              There was a problem loading the donation form. Please try
              refreshing the page.
            </Trans>
          </div>
        </div>
        <div id="div-iframe-holder"></div>
      </Fragment>
    )
  }
)

export interface CustomState {
  anLink: string
  anIdentifier: string
}

export const FundraiserAction: React.FC<{
  state: CustomState
  language: string
}> = memo(({ state, language }) => {
  let attempts = 0

  const timerId = useRef(setTimeout(() => function() {}, 0))

  let filteredAnLink = ""
  let filteredAnIdentifier = ""

  try {
    const { anLink } = state
    const { anIdentifier } = state

    if (anLink.startsWith("https://actionnetwork.org")) {
      filteredAnLink = anLink
    }
    if (anIdentifier) {
      filteredAnIdentifier = anIdentifier
    }
  } catch (err) {}

  const showExtraText = () => {
    const divIframeHolder = document.getElementById("div-iframe-holder")
    const formCover = document.getElementById("form-cover")

    if (
      divIframeHolder &&
      formCover &&
      divIframeHolder.firstChild?.firstChild
    ) {
      if (!formCover.classList.contains("hidden")) {
        formCover.classList.add("hidden")
      }
    } else if (formCover) {
      if (!formCover.classList.contains("loaded")) {
        formCover.classList.add("loaded")
      }
      if (formCover.classList.contains("hidden")) {
        formCover.classList.remove("hidden")
      }
    }
  }

  const resetExtraText = () => {
    const formCover = document.getElementById("form-cover")

    if (formCover) {
      if (formCover.classList.contains("loaded")) {
        formCover.classList.remove("loaded")
      }

      if (formCover.classList.contains("hidden")) {
        formCover.classList.remove("hidden")
      }
    }
  }

  const handleSupportAction = () => {
    const scriptHolder = document.getElementById("script-holder")
    const divIframeHolder = document.getElementById("div-iframe-holder")

    if (scriptHolder && divIframeHolder) {
      const scriptSrc = filteredAnLink
      const divId = filteredAnIdentifier

      scriptHolder.innerHTML = ""
      divIframeHolder.innerHTML = ""

      const divTag = document.createElement("div")
      divTag.style.width = "100%"
      divTag.id = divId

      divIframeHolder.appendChild(divTag)

      const scriptTag = document.createElement("script")
      scriptTag.src = scriptSrc

      scriptHolder.appendChild(scriptTag)

      //console.log("Cleared in handleSupportAction.");
      clearTimeout(timerId.current)

      timerId.current = setTimeout(function() {
        if (
          attempts < 5 &&
          filteredAnIdentifier != "" &&
          filteredAnLink != "" &&
          divTag &&
          divTag.innerHTML == ""
        ) {
          attempts = attempts + 1
          handleSupportAction()
        } else {
          showExtraText()
        }
      }, 2500)
      //console.log("Set in handleSupportAction.");
    }
  }

  const cleanActions = () => {
    const scriptHolder = document.getElementById("script-holder")
    const divIframeHolder = document.getElementById("div-iframe-holder")

    if (scriptHolder && divIframeHolder) {
      scriptHolder.innerHTML = ""
      divIframeHolder.innerHTML = ""
    }
  }

  // useEffect(() => {
  //   resetExtraText();

  //   clearTimeout(timerId.current);

  //   timerId.current = setTimeout(
  //     function () {
  //       handleSupportAction();
  //     }, 6000
  //   )

  //   return function cleanup() {
  //     clearTimeout(timerId.current);
  //   }
  // }, [language])

  useEffect(() => {
    resetExtraText()

    //console.log("Cleared in useEffect.");
    clearTimeout(timerId.current)

    timerId.current = setTimeout(function() {
      handleSupportAction()
    }, 2000)
    //console.log("Set in useEffect.");

    return function cleanup() {
      //console.log("Cleared and cleaned in useEffect cleanup.");
      clearTimeout(timerId.current)
      cleanActions()
    }
  })

  return (
    <Fragment>
      <Helmet
        style={[
          {
            cssText: `
            #logo_wrap,
            .can_embed #can_main_col #donation_recipient_wrap,
            .can_embed #can_main_col #donate_login,
            .can_embed .can_thank_you_wrap .payment-info__wrap,
            .can_embed .can_thank_you_wrap .clearfix,
            .can_embed div.affirmative_note {
              display: none;
            }

            .can_embed #can_main_col li.cc_li_half {
              display: inline;
            }

            .can_embed #can_main_col ul.clearfix {
              padding-left: 0px;
            }

            #can_embed_form h2 {
              font-size: 2em;
              letter-spacing: -0.8px;
              margin-bottom: 1em;
            }

            #can_embed_form #can_sidebar .action_widget.action_status_tracker {
              margin-top: 0.5em;
              margin-bottom: 1em;
            }

            #can_embed_form #can_sidebar .action_status_running_total {
              line-height: 2em;
              font-size: 1.4em;
              font-weight: 600;
            }

            #can_embed_form .action_status_goal {
              font-family: "IBM Plex Mono", Menlo, monospace;
              line-height: 2em;
              font-size: 1.1em;
              color: #6a6a6a;
            }

            #can_embed_form #can_sidebar .action_sidebar {
              width: 100%;
            }

            #can_embed_form #can_sidebar .action_sidebar img {
              width: 100%;
            }

            #can_embed_form #can_sidebar .action_sidebar .action_description {
              font-size: 1.1em;
            }

            #can_embed_form #can_sidebar .action_sidebar #action_info {
              display: none;
            }

            #can_embed_form #can_sidebar .action_widget .action_status_status_bar {
              width: 100%;
              height: 10px;
              font-size: 0;
              background-color: #FED0DB;
              border-radius: 100px;
            }

            #can_embed_form #can_sidebar .action_widget .action_status_status_bar span {
              display: inline-block;
              height: 100%;
              background-color: #FC164B;
              border-radius: 100px;
            }

            #can_embed_form #can_sidebar .action_widget .action_status_status_bar span span {
              height: 100%;
              width: 100%;
              display: inline-block;
            }

            .can_embed #can_main_col li.cc_li_quarter:nth-child(7) {
              margin-top: 16px;
              width: 100%;
              margin-bottom: 30px;
              grid-template-columns: 1fr;
              display: grid;
            }

            .can_embed #can_main_col #cc_state_drop_wrap {
              border: 1px solid black;
              margin: 0px;
              width: 100%;
              display: block;
              padding: 7.5px;
              border-radius: 4px;
              margin-bottom: 15px;
              white-space: nowrap;
            }

            .can_embed #can_main_col #cc_state_drop_wrap #form-state {
              height: 20px !important;
              width: 100% !important;
            }

            .can_embed #can_main_col span.international_link-wrap {
              overflow: unset !important
            }

            .can_embed #can_main_col span.international_link-wrap > a.international_link {
              font-family: "IBM Plex Mono", Menlo, monospace;
              color: #FC164B;
              text-decoration: underline;
            }

            .can_embed #can_main_col span.international_link-wrap > a.international_link:hover {
              font-weight: 600;
              cursor: pointer;
            }
            
            .can_embed #can_main_col .country_drop_wrap {
              border: 1px solid black;
              margin: 0px;
              width: 100%;
              display: block;
              padding: 7.5px;
              border-radius: 4px;
            }
            
            .can_embed #can_main_col .country_drop_wrap #form-country {
              width: 100% !important;
              height: 20px !important;
            }

            .can_embed #can_main_col .country_drop_wrap span.can_selectInner {
              white-space: nowrap;
            }
            
            .can_embed #can_main_col .floatlabel-wrapper {
              margin-top: 15px;
              display: inline-block;
              margin-bottom: 5px;
              width: 100%;
            }
            
            .can_embed #can_main_col .floatlabel-wrapper label {
              width: 100%;
              color: #280000;
              font-size: 18px;
              font-weight: 600;
              letter-spacing: -0.3px;
            }
            
            .can_embed #can_main_col .floatlabel-wrapper input {
              width: 100%;
              appearance: none;
              border-bottom-width: 2px;
              border-bottom-style: solid;
              border-bottom-color: #280000;
              border-top: none;
              border-left: none;
              border-right: none;
              font-size: 16px;
              background-color: rgba(0, 0, 0, 0);
              line-height: 22px;
              font-family: neue-haas-grotesk-display, "Neue Haas", "Helvetica Neue", Helvetica, system-ui, sans-serif;
              margin-top: 8px;
            }
            
            .can_embed #can_main_col .floatlabel-wrapper input:focus {
              outline: none;
            }
            
            .can_embed #can_main_col .floatlabel-wrapper input::placeholder,
            .can_embed #can_main_col .floatlabel-wrapper input::-webkit-input-placeholder,
            .can_embed #can_main_col .floatlabel-wrapper input:-moz-placeholder,
            .can_embed #can_main_col .floatlabel-wrapper input::-moz-placeholder,
            .can_embed #can_main_col .floatlabel-wrapper input:-ms-input-placeholder {
              color: transparent !important;
            }

            .can_embed #can_main_col #donation_welcome {
              margin-top: 10px;
              background-color: rgb(252 22 75 / 20%);
              padding: 1.2em;
              border-radius: 15px;
            }

            .can_embed #can_main_col .donate_amount_wrap {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
              gap: 0.2em;
              padding-right: 1px;
              position: relative;
            }

            .can_embed #can_main_col .donate_amount_wrap label {
              padding: 15px 5px 15px 5px;
              background-color: white;
              border: 2px solid #FC164B;
              border-radius: 8px;
              text-align: center;
              font-size: 18px;
              align-self: center;
              height: 55px;
              font-weight: 500;
              cursor: pointer;
              white-space: nowrap;
              line-height: 24px;
            }

            .can_embed #can_main_col .donate_amount_wrap label:hover,
            .can_embed #can_main_col .donate_amount_wrap .donate_amount-selected {
              background-color: #FC164B;
              color: white;
            }

            .can_embed #can_main_col .donate_amount_wrap label:active {
              background-color: darkred;
              border-color: darkred;
            }
            
            .can_embed #can_main_col .donate_amount_wrap input[type=radio] {
              visibility: hidden;
              position: absolute;
              top: 2px;
              left: 2px;
            }
            
            .can_embed #can_main_col .donate_amount_wrap input[type=text] {
              max-width: 50px;
              line-height: 18px;
              font-size: 18px;
              height: 20px;
              border-width: 0px;
              font-weight: 500;
              background-color: inherit;
              color: inherit;
              text-align: center;
            }
            
            .can_embed #can_main_col .donate_recurring {
              line-height: 58px;
              color: #280000;
              margin-bottom: 30px;
              margin-top: 2.5px;
              height: 60px;
              width: 100%;
              -webkit-user-select: none;    
              -moz-user-select: none;
              -ms-user-select: none;
              user-select: none;
              cursor: pointer;
              position: relative;
            }

            .can_embed #can_main_col .donate_recurring .donate_recurring_inner {
              position: absolute;
              z-index: 1;
              width: 100%;
            }

            .can_embed #can_main_col .donate_recurring .donate_recurring_inner label {
              position: absolute;
              width: 100%;
              text-align: center;
              cursor: pointer !important;
              font-size: 18px
            }

            .can_embed #can_main_col .donate_recurring .donate_recurring_inner label[autofocus=false] {
              background-color: red;
            }
            
            .can_embed #can_main_col .donate_recurring input[type=checkbox] {
              vertical-align: middle;
              appearance: none;
              font-size: 60px;
              background: white;
              border: 2px solid #FC164B;
              width: 100%;
              height: 55px;
              border-radius: 8px;
              -webkit-box-pack: center;
              justify-content: center;
              -webkit-box-align: center;
              margin: 0px;
              position: absolute;
              z-index: -1;
              left: 0px;
            }
            
            .can_embed #can_main_col .donate_recurring input[type=checkbox]:checked {
              border: 6px solid #FC164B;
            }

            .can_embed #can_main_col li.cc_li_third:nth-child(10),
            .can_embed #can_main_col li.cc_li_third:nth-child(12) {
              padding: 10px;
              padding-left: 0px;
              height: 54.7px;
              width: 55%;
              margin-top: 38px;
              font-size: 17px;
              display: inline-block;
              float: right;
            }
            
            .can_embed #can_main_col li.cc_li_third:nth-child(10) #form-month,
            .can_embed #can_main_col li.cc_li_third:nth-child(10) #form-year,
            .can_embed #can_main_col li.cc_li_third:nth-child(12) #form-month,
            .can_embed #can_main_col li.cc_li_third:nth-child(12) #form-year {
              width: 70px;
              height: 18px !important;
            }
            
            .can_embed #can_main_col li.cc_li_third:nth-child(10) span.donate_cc_month,
            .can_embed #can_main_col li.cc_li_third:nth-child(12) span.donate_cc_month {
              border-bottom: 2px solid black;
              margin-right: 10px;
            }
            
            .can_embed #can_main_col li.cc_li_third:nth-child(10) span.donate_cc_month span.can_select,
            .can_embed #can_main_col li.cc_li_third:nth-child(12) span.donate_cc_month span.can_select {
              width: 55px;
            }
            
            .can_embed #can_main_col li.cc_li_third:nth-child(10) span.donate_cc_month span.can_select span.can_selectInner,
            .can_embed #can_main_col li.cc_li_third:nth-child(10) span.donate_cc_year span.can_select span.can_selectInner,
            .can_embed #can_main_col li.cc_li_third:nth-child(12) span.donate_cc_month span.can_select span.can_selectInner,
            .can_embed #can_main_col li.cc_li_third:nth-child(12) span.donate_cc_year span.can_select span.can_selectInner {
              width: 70px;
            }
            
            .can_embed #can_main_col li.cc_li_third:nth-child(10) span.donate_cc_year,
            .can_embed #can_main_col li.cc_li_third:nth-child(12) span.donate_cc_year {
              border-bottom: 2px solid black;
            }
            
            .can_embed #can_main_col li.cc_li_third:nth-child(10) span.donate_cc_year span.can_select,
            .can_embed #can_main_col li.cc_li_third:nth-child(12) span.donate_cc_year span.can_select {
              width: 60px;
            }
            
            .can_embed #can_main_col li.cc_li_third:nth-child(11):not(.clear),
            .can_embed #can_main_col li.cc_li_third:nth-child(13) {
              margin-bottom: 5px;
              width: 45%;
              float: left;
            }

            .can_embed #can_main_col li.cc_li_third:nth-child(11) label.floatlabel-label,
            .can_embed #can_main_col li.cc_li_third:nth-child(13) label.floatlabel-label {
              white-space: nowrap;
            }
            
            .can_embed #can_main_col .floatlabel-wrapper label {
              width: 100%;
              color: #280000;
              font-size: 18px;
              font-weight: 600;
              letter-spacing: -0.4px;
            }
            
            .can_embed #can_main_col .floatlabel-wrapper input#form-card_sec {
              width: 94%;
            }
            
            .can_embed #can_main_col .floatlabel-wrapper input {
              width: 100%;
              appearance: none;
              border-bottom-width: 2px;
              border-bottom-style: solid;
              border-bottom-color: #280000;
              border-top: none;
              border-left: none;
              border-right: none;
              font-size: 16px;
              background-color: rgba(0, 0, 0, 0);
              line-height: 22px;
              font-family: neue-haas-grotesk-display, "Neue Haas", "Helvetica Neue", Helvetica, system-ui, sans-serif;
              margin-top: 8px;
            }
            
            .can_embed #can_main_col li.form_builder_output {
              display: none;
            }

            .can_embed #can_main_col li.core_field {
              max-width: 250px;
              margin-bottom: 30px;
            }
            
            .can_embed #can_main_col #donate_form_button {
              margin-top: 15px;
              appearance: none;
              font-size: 18px;
              text-align: center;
              line-height: 20px;
              padding: 8px 16px;
              background-color: #ffffff;
              border: 2px solid #FC164B;
              color: #FC164B;
              border-radius: 100px;
              text-decoration: none;
              font-weight: 500;
              user-select: none;
              outline: none;
              cursor: pointer;
              letter-spacing: -0.3px;
              white-space: break-spaces;
            }

            .can_embed #can_main_col #d_sharing {
              color: #280000;
            }
            
            .can_embed #can_main_col #d_sharing::marker {
              font-size: 0;
            }

            .can_embed #can_main_col #d_sharing ul,
            .can_embed #can_main_col #d_sharing li {
              padding-left: 0px;
            }

            .can_embed #can_main_col #d_sharing .control-group::marker {
              font-size: 0;
            }

            .can_embed #can_main_col #d_sharing .control-group .control-label{
              margin-bottom: 10px;
            }

            .can_embed #can_main_col #d_sharing .control-group span.controls.check_radio_field {
              display: block;
              margin-bottom: 6px;
            }

            .can_embed #can_main_col #d_sharing .control-group span.controls.check_radio_field > :first-child {
              margin-right: 8px;
              margin-bottom: 4px;
            }

            .can_embed #can_main_col #d_sharing .control-group span.controls.check_radio_field label.radio {
              white-space: nowrap;
              line-height: 35px;
            }

            .can_embed #can_main_col #d_sharing .control-group span.controls.check_radio_field label.radio input[type=radio] {
              vertical-align: middle;
              cursor: pointer;
              appearance: none;
              font-size: 60px;
              background: white;
              border: 2px solid white;
              outline: 2px solid #FC164B;
              width: 20px;
              height: 20px;
              border-radius: 20%;
              -webkit-box-pack: center;
              justify-content: center;
              -webkit-box-align: center;
              margin: 2px;
              margin-left: 1.5px;
              margin-right: 5px;
            }

            .can_embed #can_main_col #d_sharing .control-group span.controls.check_radio_field label.radio input[type=radio]:checked {
              background: #FC164B;
              border: 2px solid #FED0DB;
              outline: 2px solid #FC164B;
            }

            .can_embed div#donation_welcome > img {
              display: none;
            }

            .can_embed div#donation_welcome div.left {
              font-size: 1.1em;
            }

            .can_embed div#donation_welcome div.left div {
              margin-bottom: 15px;
            }

            .can_embed div#donation_welcome a {
              color: #280000;
              text-decoration-color: #FC164B;
            }

            .can_embed div#donation_welcome a:hover {
              color: #FC164B;
            }

            #form-styling-container {
              position: relative;
              height: 100%;
              width: 100%;
              min-height: 200px;
            }
            
            #form-styling-container #form-cover.hidden,
            #form-styling-container #form-cover.loaded #loading-text,
            #form-styling-container #form-cover #error-message {
              display: none;
            }
            
            #form-styling-container #form-cover.loaded #error-message {
              display: block;
            }
            
            #form-styling-container #form-cover {
              position: absolute;
              height: 100%;
              width: 100%;
              background-color: #F7F4F1;
            }
            
            #form-styling-container #form-cover #loading-text {
              width: 100%;
              text-align: center;
              font-size: 30px;
              line-height: 50px;
            }
            
            #form-styling-container #form-cover #error-message {
              width: 100%;
              text-align: center;
              font-size: 30px;
              line-height: 50px;
            }

            @media screen and (max-width: 390px) {
              .can_embed #can_main_col .donate_amount_wrap {
                grid-template-columns: 1fr 1fr 1fr;
              }
            }
            `,
          },
        ]}
      />
      <div id="form-styling-container">
        <div id="script-holder"></div>
        <div id="form-cover">
          <div id="loading-text">
            <Spinner />
          </div>
          <div id="error-message" className="hidden">
            There was a problem loading the fundraiser donation form. Please try
            reloading the page from the original article.
          </div>
        </div>
        <div
          id="div-iframe-holder"
          sx={{ fontFamily: "'IBM Plex Mono', Menlo, monospace" }}
        ></div>
      </div>
    </Fragment>
  )
})

export const ActionBox: React.FC<{
  anData: ActionData
  type: string
}> = memo(({ anData, type }) => {
  const { anLink, anIdentifier, anSize, anType } = anData

  const dir = useTextDirection()

  let attempts = 0

  const timerId = useRef(setTimeout(() => function() {}, 0))

  let filteredColour = "#FC164B"

  if (type == "wire-articles") {
    filteredColour = "#00bf89"
  } else if (type == "movement-campaigns") {
    filteredColour = "#c600e2"
  } else if (type == "blueprint-documents") {
    filteredColour = "#3f27ff"
  }

  const lighterColour = colourMidpoint(filteredColour, "#ffffff")

  let filteredActionLink = ""
  let filteredActionIdentifier = ""

  let heightOffset = 0

  if (anSize) {
    if (anSize == "sml") {
      heightOffset = -50
    } else if (anSize == "lrg") {
      heightOffset = 50
    }
  }

  if (anLink && anLink.startsWith("https://actionnetwork.org")) {
    filteredActionLink = anLink
  }

  if (anIdentifier) {
    filteredActionIdentifier = anIdentifier
  }

  const parseParams = qs.parse(
    typeof window === `undefined` ? "" : window.location.search
  )

  const queryParams = parseParams != null ? parseParams : {}

  const validParams = (({
    link_id,
    can_id,
    source,
    email_referrer,
    email_subject,
  }) => ({ link_id, can_id, source, email_referrer, email_subject }))(
    queryParams
  )

  const payload = qs.stringify(validParams)

  const toggleSpinner = (power: boolean) => {
    const fundraiserCover = document.getElementById("fundraiser-cover")

    if (fundraiserCover) {
      if (power == false && !fundraiserCover.classList.contains("hidden")) {
        fundraiserCover.classList.add("hidden")
      } else if (
        power == true &&
        fundraiserCover.classList.contains("hidden")
      ) {
        fundraiserCover.classList.remove("hidden")
      }
    }
  }

  const handleAction = (inputActionLink: string) => {
    const scriptHolder = document.getElementById("script-holder")
    const divIframeHolder = document.getElementById(filteredActionIdentifier)

    if (scriptHolder && divIframeHolder) {
      scriptHolder.innerHTML = ""
      divIframeHolder.innerHTML = ""

      const scriptTag = document.createElement("script")
      scriptTag.src = inputActionLink

      scriptHolder.appendChild(scriptTag)

      //console.log("Cleared in handleAction.");
      clearTimeout(timerId.current)

      timerId.current = setTimeout(function() {
        if (attempts < 5 && divIframeHolder.innerHTML == "") {
          attempts = attempts + 1
          handleAction(inputActionLink)
        } else if (divIframeHolder.innerHTML != "") {
          toggleSpinner(false)
        }
      }, 2500)
      //console.log("Set in handleAction.");
    }
  }

  const cleanActions = () => {
    const scriptHolder = document.getElementById("script-holder")
    const divIframeHolder = document.getElementById(filteredActionIdentifier)

    if (scriptHolder && divIframeHolder) {
      scriptHolder.innerHTML = ""
      divIframeHolder.innerHTML = ""
    }
  }

  useEffect(() => {
    try {
      oneTimeReload()
    } catch (err) {
      //console.log("Reload issue.");
    }
  }, [])

  useEffect(() => {
    toggleSpinner(true)

    //console.log("Cleared in useEffect.");
    clearTimeout(timerId.current)

    timerId.current = setTimeout(function() {
      handleAction(filteredActionLink)
    }, 2000)
    //console.log("Set in useEffect.");

    return function cleanup() {
      //console.log("Cleared and cleaned in useEffect cleanup.");
      clearTimeout(timerId.current)
      cleanActions()
    }
  })

  return (
    <Box
      sx={{
        backgroundColor: "white",
        position: "relative",
        zIndex: "1",
        paddingTop: "2em",
        paddingBottom: "2em",
      }}
    >
      <div
        sx={{
          backgroundColor: "white",
          width: "1000%",
          marginLeft: "-450%",
          position: "absolute",
          top: "0px",
          bottom: "0px",
          zIndex: "-1",
        }}
      ></div>
      <Helmet
        style={[
          {
            cssText: ActionBoxStyling(
              anType,
              heightOffset,
              lighterColour,
              filteredColour
            ),
          },
        ]}
      />
      <div
        id="fundraiser-cover"
        sx={{
          position: "absolute",
          backgroundColor: "white",
          top: "0",
          bottom: "0",
          left: "-10px",
          right: "-10px",
          color: "#280000",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spinner />
      </div>
      <div
        id="script-holder"
        sx={{ position: "relative", top: "-150px" }}
      ></div>
      <div
        id={filteredActionIdentifier}
        sx={{
          maxHeight: "1250px",
          fontFamily: '"IBM Plex Mono", Menlo, monospace',
        }}
      ></div>
      {anType == "fundraiser" && (
        <GatsbyLink
          to={`/fundraiser?${payload}`}
          state={{
            anLink: filteredActionLink,
            anIdentifier: filteredActionIdentifier,
          }}
        >
          <Button
            variant={"filled"}
            sx={{
              width: ["100%", "60%", "40%"],
              maxWidth: "100%",
              marginBottom: "10px",
              marginTop: "10px",
              fontSize: "1.4em",
              bg: filteredColour,
            }}
          >
            <Trans>Donate</Trans>
          </Button>
        </GatsbyLink>
      )}
    </Box>
  )
})

export const ObservatorySignupAction: React.FC<{ language: string }> = memo(
  ({ language }) => {
    let attempts = 0

    const timerId = useRef(setTimeout(() => function() {}, 0))

    const showExtraText = () => {
      const divIframeHolder = document.getElementById("div-iframe-holder")
      const extraTextHolder = document.getElementById("extra-text")
      const formCover = document.getElementById("form-cover")

      if (
        divIframeHolder &&
        extraTextHolder &&
        formCover &&
        divIframeHolder.firstChild?.firstChild
      ) {
        if (extraTextHolder.classList.contains("hidden")) {
          extraTextHolder.classList.remove("hidden")
        }
        if (!formCover.classList.contains("hidden")) {
          formCover.classList.add("hidden")
        }
      } else if (formCover) {
        if (!formCover.classList.contains("loaded")) {
          formCover.classList.add("loaded")
        }
        if (formCover.classList.contains("hidden")) {
          formCover.classList.remove("hidden")
        }
      }
    }

    const resetExtraText = () => {
      const extraTextHolder = document.getElementById("extra-text")
      const formCover = document.getElementById("form-cover")

      if (extraTextHolder && formCover) {
        if (!extraTextHolder.classList.contains("hidden")) {
          extraTextHolder.classList.add("hidden")
        }

        if (formCover.classList.contains("loaded")) {
          formCover.classList.remove("loaded")
        }

        if (formCover.classList.contains("hidden")) {
          formCover.classList.remove("hidden")
        }
      }
    }

    const handleObservatoryAction = () => {
      const scriptHolder = document.getElementById("script-holder")
      const divIframeHolder = document.getElementById("div-iframe-holder")

      if (scriptHolder && divIframeHolder) {
        let scriptSrc =
          "https://actionnetwork.org/widgets/v5/form/progressive-international-observatory?format=js&source=widget"
        let divId = "can-form-area-progressive-international-observatory"

        if (language == "es") {
          scriptSrc =
            "https://actionnetwork.org/widgets/v5/form/observatorio-de-la-internacional-progresista?format=js&source=widget"
          divId = "can-form-area-observatorio-de-la-internacional-progresista"
        } else if (language == "pt-BR") {
          scriptSrc =
            "https://actionnetwork.org/widgets/v5/form/progressive-international-observatory-2?format=js&source=widget"
          divId = "can-form-area-progressive-international-observatory-2"
        }

        scriptHolder.innerHTML = ""
        divIframeHolder.innerHTML = ""

        const divTag = document.createElement("div")
        divTag.style.width = "100%"
        divTag.id = divId

        divIframeHolder.appendChild(divTag)

        const scriptTag = document.createElement("script")
        scriptTag.src = scriptSrc

        scriptHolder.appendChild(scriptTag)

        //console.log("Cleared in handleSupportAction.");
        clearTimeout(timerId.current)

        timerId.current = setTimeout(function() {
          if (attempts < 5 && divTag && divTag.innerHTML == "") {
            attempts = attempts + 1
            handleObservatoryAction()
          } else {
            showExtraText()
          }
        }, 2500)
        //console.log("Set in handleSupportAction.");
      }
    }

    const cleanActions = () => {
      const scriptHolder = document.getElementById("script-holder")
      const divIframeHolder = document.getElementById("div-iframe-holder")

      if (scriptHolder && divIframeHolder) {
        scriptHolder.innerHTML = ""
        divIframeHolder.innerHTML = ""
      }
    }

    useEffect(() => {
      try {
        oneTimeReload()
      } catch (err) {
        //console.log("Reload issue.");
      }
    }, [language])

    useEffect(() => {
      resetExtraText()

      //console.log("Cleared in useEffect.");
      clearTimeout(timerId.current)

      timerId.current = setTimeout(function() {
        handleObservatoryAction()
      }, 2000)
      //console.log("Set in useEffect.");

      return function cleanup() {
        //console.log("Cleared and cleaned in useEffect cleanup.");
        clearTimeout(timerId.current)
        cleanActions()
      }
    })

    return (
      <Fragment>
        <Helmet
          style={[
            {
              cssText: `
              .can_embed li {
                display: inline;
              }

              .can_embed li::marker {
                font-size: 0px;
              }

              .can_embed #action_info,
              .can_embed #logo_wrap,
              .can_embed #form_col2 img.ajax-loading,
              .can_embed div.affirmative_note {
                display: none;
              }

              .can_embed #can_embed_form_inner > h2,
              .can_embed #can_embed_form_inner > h4 {
                display: none;
              }

              .can_embed #form_col1 li.core_field > .floatlabel-wrapper > label.floatlabel-label {
                font-size: 0;
                display: none;
              }

              .can_embed #form_col1 li.core_field > .floatlabel-wrapper > input.floatlabel-input,
              .can_embed #form_col1 li.core_field > .floatlabel-wrapper > div.iti > input#form-phone {
                font-size: 1.1em;
                width: 100%;
                height: 2.5em;
                border: none;
                margin-top: 0.2em;
                margin-bottom: 0.2em;
                border-radius: 0.6em;
                padding: 0.5em;
                line-height: 2.5em;
                font-weight: 600;
              }

              .can_embed #form_col1 li.core_field > .floatlabel-wrapper > div.iti {
                margin-bottom: 0.5em;
              }

              .can_embed #form_col1 li#mobile_opt_in {
                font-size: 0.9em;
              }

              .can_embed #form_col1 li#mobile_opt_in input#mobile_opt_in_checkbox[type=checkbox] {
                vertical-align: middle;
                cursor: pointer;
                appearance: none;
                font-size: 60px;
                background: white;
                border: 2px solid white;
                outline: 2px solid #FC164B;
                width: 20px;
                height: 20px;
                border-radius: 20%;
                -webkit-box-pack: center;
                justify-content: center;
                -webkit-box-align: center;
                margin: 2px;
                margin-left: 1.5px;
                margin-right: 5px;
              }

              .can_embed #form_col1 li#mobile_opt_in input#mobile_opt_in_checkbox[type=checkbox]:checked {
                background: #FC164B;
                border: 2px solid #FED0DB;
                outline: 2px solid #FC164B;
              }

              .can_embed #form_col1 li#mobile_opt_in span#mobile_opt_in_default {
                line-height: 1.1em;
              }

              .can_embed #form_col1 span.international_link-wrap {
                display: block;
                line-height: 1.5em;
              }

              .can_embed #form_col1 span.international_link-wrap > a.international_link {
                font-family: "IBM Plex Mono", Menlo, monospace;
                color: #FC164B;
                text-decoration: underline;
              }

              .can_embed #form_col1 span.international_link-wrap > a.international_link:hover {
                font-weight: 600;
                cursor: pointer;
              }

              .can_embed #form_col1 > div#action_welcome_message {
                margin-bottom: 1.5em;
              }

              .can_embed #form_col2 {
                margin-top: 2em;
              }

              .can_embed #form_col2 input[name=commit] {
                appearance: none;
                text-align: center;
                line-height: 2em;
                background-color: #ffffff;
                border: 2px solid #FC164B;
                color: #FC164B;
                border-radius: 100px;
                text-decoration: none;
                font-weight: 500;
                user-select: none;
                outline: none;
                cursor: pointer;
                letter-spacing: -0.3px;
                white-space: break-spaces;
                font-size: 1.1em;
                padding: 0em 0.7em;
              }

              .can_embed #form_col2 input[name=commit]:hover {
                background-color: #FC164B;
                color: #ffffff;
              }

              .can_embed #form_col1 span.country_drop_wrap.hide {
                display: none;
              }

              .can_embed #form_col1 span.country_drop_wrap {
                border: 1px solid black;
                margin: 0px;
                width: 100%;
                display: block;
                padding: 7.5px;
                border-radius: 4px;
                margin-top: 0.5em;
                margin-bottom: 1em;
              }

              .can_embed #form_col1 span.country_drop_wrap #form-country {
                width: 100% !important;
                height: 20px !important;
              }

              .can_embed #form_col1 span.country_drop_wrap span.can_selectInner {
                white-space: nowrap;
              }

              .can_embed #form_col2 li#d_sharing > ul {
                padding-left: 0px;
                margin-top: 0px;
              }

              .can_embed #form_col2 li#d_sharing > ul > li.control-group.js-affirmative_optin_control_group {
                font-size: 0.9em;
              }

              .can_embed #form_col2 li#d_sharing > ul > div.affirmative_note > small > em {
                font-family: "IBM Plex Mono", Menlo, monospace;
                font-style: normal;
                color: #694d4d;
              }

              .can_embed #form_col2 li#d_sharing .control-group .control-label {
                margin-bottom: 10px;
              }

              .can_embed #form_col2 li#d_sharing .control-group span.controls.check_radio_field {
                display: block;
                margin-bottom: 6px;
              }

              .can_embed #form_col2 li#d_sharing .control-group span.controls.check_radio_field > :first-child {
                margin-right: 8px;
                margin-bottom: 4px;
              }

              .can_embed #form_col2 li#d_sharing .control-group span.controls.check_radio_field label.radio {
                white-space: nowrap;
                line-height: 35px;
              }

              .can_embed #form_col2 li#d_sharing .control-group span.controls.check_radio_field label.radio input[type=radio] {
                vertical-align: middle;
                cursor: pointer;
                appearance: none;
                font-size: 60px;
                background: white;
                border: 2px solid white;
                outline: 2px solid #FC164B;
                width: 20px;
                height: 20px;
                border-radius: 20%;
                -webkit-box-pack: center;
                justify-content: center;
                -webkit-box-align: center;
                margin: 2px;
                margin-left: 1.5px;
                margin-right: 5px;
              }

              .can_embed #form_col2 li#d_sharing .control-group span.controls.check_radio_field label.radio input[type=radio]:checked {
                background: #FC164B;
                border: 2px solid #FED0DB;
                outline: 2px solid #FC164B;
              }

              #form-styling-container {
                position: relative;
                height: 100%;
                width: 100%;
                min-height: 200px;
              }
              
              #form-styling-container #form-cover.hidden,
              #form-styling-container #form-cover.loaded #loading-text,
              #form-styling-container #form-cover #error-message {
                display: none;
              }
              
              #form-styling-container #form-cover.loaded #error-message {
                display: block;
              }
              
              #form-styling-container #form-cover {
                position: absolute;
                height: 100%;
                width: 100%;
                background-color: #f3e5e0;
                z-index: 1;
              }
              
              #form-styling-container #form-cover #loading-text {
                width: 100%;
                height: 100%;
                text-align: center;
                font-size: 30px;
                line-height: 50px;
                color: #280000;
                display: flex;
                padding-bottom: 1em;
              }

              #form-styling-container #form-cover #loading-text svg {
                display: block;
                margin: auto;
              }
              
              #form-styling-container #form-cover #error-message {
                width: 100%;
                text-align: center;
                font-size: 30px;
                line-height: 50px;
              }
              
              #extra-text {
                position: relative;
                width: 100%;
                margin-top: 10px;
              }
              
              #extra-text.hidden .main-heading,
              #extra-text.hidden .details-heading {
                display: none;
              }
              
              #extra-text .main-heading {
                font-size: 24px;
                font-weight: 600;
                line-height: 110%;
                letter-spacing: -0.3px;
                margin-bottom: 0.5em;
              }
              
              #extra-text .details-heading {
                font-size: 24px;
                font-weight: 600;
                line-height: 110%;
                letter-spacing: -0.3px;
                margin-bottom: 0.5em;
                position: absolute;
                left: 0px;
                top: 164px;
              }

              #can_embed_form input::placeholder {
                color: #6a6a6a;
              }
        
              #can_embed_form input::-webkit-input-placeholder {
                color: #6a6a6a;
              }
        
              #can_embed_form input:-moz-placeholder {
                color: #6a6a6a;
              }
        
              #can_embed_form input::-moz-placeholder {
                color: #6a6a6a;
              }
        
              #can_embed_form input:-ms-input-placeholder {
                color: #6a6a6a;
              }
            `,
            },
          ]}
        />
        <div id="form-styling-container">
          <div id="script-holder"></div>
          <div id="extra-text" className="hidden"></div>
          <div id="form-cover">
            <div id="loading-text">
              <Spinner />
            </div>
            <div id="error-message" className="hidden">
              There was a problem loading the form. Please try refreshing the
              page.
            </div>
          </div>
          <div id="div-iframe-holder"></div>
        </div>
      </Fragment>
    )
  }
)

export const ActionSidebar: React.FC<{
  anData: ActionData
  type: string
}> = memo(({ anData, type }) => {
  const { anLink, anIdentifier, anSize, anType } = anData

  const dir = useTextDirection()

  let attempts = 0

  const timerId = useRef(setTimeout(() => function() {}, 0))

  let filteredColour = "#FC164B"

  if (type == "wire-articles") {
    filteredColour = "#00bf89"
  } else if (
    type == "movement-campaigns" ||
    type == "movement-actions" ||
    type == "movement-articles"
  ) {
    filteredColour = "#c600e2"
  } else if (type == "blueprint-documents") {
    filteredColour = "#3f27ff"
  } else if (type == "observatory-articles") {
    filteredColour = "#ff0045"
  } else {
    return "#280000"
  }

  const lighterColour = colourMidpoint(filteredColour, "#ffffff")

  let filteredActionLink = ""
  let filteredActionIdentifier = ""

  let heightOffset = 0

  if (anSize) {
    if (anSize == "sml") {
      heightOffset = -50
    } else if (anSize == "lrg") {
      heightOffset = 50
    }
  }

  if (anLink && anLink.startsWith("https://actionnetwork.org")) {
    filteredActionLink = anLink
  }

  if (anIdentifier) {
    filteredActionIdentifier = anIdentifier
  }

  const toggleSpinner = (power: boolean) => {
    const actionCover = document.getElementById("action-cover")

    if (actionCover) {
      if (power == false && !actionCover.classList.contains("hidden")) {
        actionCover.classList.add("hidden")
      } else if (power == true && actionCover.classList.contains("hidden")) {
        actionCover.classList.remove("hidden")
      }
    }
  }

  const handleAction = (inputActionLink: string) => {
    const scriptHolder = document.getElementById("script-holder")
    const divIframeHolder = document.getElementById(filteredActionIdentifier)

    if (scriptHolder && divIframeHolder) {
      scriptHolder.innerHTML = ""
      divIframeHolder.innerHTML = ""

      const scriptTag = document.createElement("script")
      scriptTag.src = inputActionLink

      scriptHolder.appendChild(scriptTag)

      //console.log("Cleared in handleAction.");
      clearTimeout(timerId.current)

      timerId.current = setTimeout(function() {
        if (attempts < 5 && divIframeHolder.innerHTML == "") {
          attempts = attempts + 1
          handleAction(inputActionLink)
        } else if (divIframeHolder.innerHTML != "") {
          toggleSpinner(false)
        }
      }, 2500)
      //console.log("Set in handleAction.");
    }
  }

  const cleanActions = () => {
    const scriptHolder = document.getElementById("script-holder")
    const divIframeHolder = document.getElementById(filteredActionIdentifier)

    if (scriptHolder && divIframeHolder) {
      scriptHolder.innerHTML = ""
      divIframeHolder.innerHTML = ""
    }
  }

  useEffect(() => {
    try {
      oneTimeReload()
    } catch (err) {
      //console.log("Reload issue.");
    }
  }, [])

  useEffect(() => {
    toggleSpinner(true)

    //console.log("Cleared in useEffect.");
    clearTimeout(timerId.current)

    timerId.current = setTimeout(function() {
      handleAction(filteredActionLink)
    }, 2000)
    //console.log("Set in useEffect.");

    return function cleanup() {
      //console.log("Cleared and cleaned in useEffect cleanup.");
      clearTimeout(timerId.current)
      cleanActions()
    }
  })

  return (
    <Box sx={{ position: "relative" }}>
      <Helmet
        style={[
          {
            cssText: ActionSidebarStyling(
              anType,
              lighterColour,
              filteredColour
            ),
          },
        ]}
      />
      <div
        id="action-cover"
        sx={{
          position: "absolute",
          backgroundColor: "white",
          top: "0",
          bottom: "0",
          left: "0",
          right: "0",
          color: "#280000",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: "1",
        }}
      >
        <Spinner />
      </div>
      <div
        id="script-holder"
        sx={{ position: "relative", top: "-150px" }}
      ></div>
      <div
        id={filteredActionIdentifier}
        sx={{
          maxHeight: "1250px",
          fontFamily: '"IBM Plex Mono", Menlo, monospace',
        }}
      ></div>
    </Box>
  )
})
